import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Building2,
  DollarSign,
  Clock,
  Send,
  MessageSquare,
  CheckCircle,
  XCircle,
  Star,
  Briefcase,
  AlertCircle,
  Plus,
  Loader2,
  ChevronRight,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOAN_TYPES = [
  { id: "fix_flip", label: "Fix & Flip" },
  { id: "bridge", label: "Bridge Loan" },
  { id: "rental", label: "Rental/DSCR" },
  { id: "construction", label: "Construction" }
];

/**
 * LendersTab - Funding request management for investors
 * Shows on Deal Detail page, allows creating funding requests
 * and communicating with interested lenders
 */
export default function LendersTab({ deal }) {
  const [fundingRequests, setFundingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedLender, setSelectedLender] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    loan_amount_requested: "",
    loan_type: "fix_flip",
    timeline_days: 30,
    notes: ""
  });

  useEffect(() => {
    if (deal?.deal_id) {
      fetchFundingRequests();
    }
  }, [deal?.deal_id]);

  const fetchFundingRequests = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/funding-requests`,
        { withCredentials: true }
      );
      setFundingRequests(res.data.funding_requests || []);
      
      // Auto-select first open request
      const openRequest = res.data.funding_requests?.find(r => r.status === "open" || r.status === "matched");
      if (openRequest) {
        setSelectedRequest(openRequest);
      }
    } catch (err) {
      console.error("Failed to fetch funding requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/funding-request`,
        {
          deal_id: deal.deal_id,
          loan_amount_requested: parseFloat(createForm.loan_amount_requested),
          loan_type: createForm.loan_type,
          timeline_days: parseInt(createForm.timeline_days),
          notes: createForm.notes
        },
        { withCredentials: true }
      );
      
      toast.success("Funding request created! Lenders will be notified.");
      setShowCreateModal(false);
      setCreateForm({ loan_amount_requested: "", loan_type: "fix_flip", timeline_days: 30, notes: "" });
      fetchFundingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create request");
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/funding-requests/${requestId}/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success("Funding request cancelled");
      fetchFundingRequests();
    } catch (err) {
      toast.error("Failed to cancel request");
    }
  };

  const handleSelectLender = async (requestId, lenderId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/funding-requests/${requestId}/select-lender/${lenderId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Lender selected! They will be notified.");
      fetchFundingRequests();
    } catch (err) {
      toast.error("Failed to select lender");
    }
  };

  const fetchMessages = async (requestId, lenderId) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/funding-requests/${requestId}/messages`,
        { withCredentials: true }
      );
      // Filter to this lender's conversation
      const lenderMessages = res.data.messages?.filter(
        m => m.sender_id === lenderId || m.recipient_id === lenderId
      ) || [];
      setMessages(lenderMessages);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRequest || !selectedLender) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/lender-message`,
        {
          funding_request_id: selectedRequest.request_id,
          recipient_type: "lender",
          recipient_id: selectedLender.lender_id,
          content: newMessage
        },
        { withCredentials: true }
      );
      setNewMessage("");
      fetchMessages(selectedRequest.request_id, selectedLender.lender_id);
    } catch (err) {
      toast.error("Failed to send message");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const openRequest = fundingRequests.find(r => r.status === "open");
  const hasActiveRequest = !!openRequest;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lenders-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Lender Marketplace</h2>
          <p className="text-sm text-white/70">
            Request funding from private lenders for this deal
          </p>
        </div>
        
        {!hasActiveRequest && (
          <Button
            onClick={() => setShowCreateModal(true)}
            className="gold-gradient-btn"
            data-testid="create-funding-request-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request Funding
          </Button>
        )}
      </div>

      {/* No Requests State */}
      {fundingRequests.length === 0 && (
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-lg p-8 text-center">
          <Building2 className="w-12 h-12 text-white/70 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-white mb-2">No Funding Requests</h3>
          <p className="text-sm text-white/70 mb-4 max-w-md mx-auto">
            Create a funding request to connect with private lenders who match your deal criteria.
          </p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="gold-gradient-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Request
          </Button>
        </div>
      )}

      {/* Active Funding Request */}
      {fundingRequests.length > 0 && (
        <div className="grid grid-cols-3 gap-6">
          {/* Left: Request List */}
          <div className="col-span-1 space-y-3">
            <h3 className="text-sm font-bold text-white/70 uppercase">Your Requests</h3>
            
            {fundingRequests.map((request) => (
              <div
                key={request.request_id}
                onClick={() => setSelectedRequest(request)}
                className={`p-4 rounded-lg border cursor-pointer transition-all duration-300 ${
                  selectedRequest?.request_id === request.request_id
                    ? "bg-[#0A0A0A]/80 backdrop-blur-sm border-gold"
                    : "bg-black border-white/[0.08] hover:border-white/[0.1]"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-white">
                    {formatCurrency(request.loan_amount_requested)}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded uppercase font-bold ${
                    request.status === "open" 
                      ? "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]"
                      : request.status === "matched"
                        ? "bg-blue-500/20 text-blue-400"
                        : request.status === "funded"
                          ? "bg-gold/20 text-gold"
                          : "bg-white/20/20 text-white/70"
                  }`}>
                    {request.status}
                  </span>
                </div>
                
                <div className="text-sm text-white/70">
                  {request.loan_type?.replace("_", " ")} • {request.timeline_days} days
                </div>
                
                {request.interested_lenders?.length > 0 && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                    <Briefcase className="w-3 h-3" />
                    {request.interested_lenders.length} lender{request.interested_lenders.length > 1 ? "s" : ""} interested
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right: Request Details & Lenders */}
          <div className="col-span-2">
            {selectedRequest ? (
              <div className="bg-black border border-white/[0.08] rounded-lg">
                {/* Request Header */}
                <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-white">
                      {formatCurrency(selectedRequest.loan_amount_requested)} Request
                    </h3>
                    <p className="text-sm text-white/70">
                      {selectedRequest.loan_type?.replace("_", " ")} • Created {new Date(selectedRequest.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  {selectedRequest.status === "open" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelRequest(selectedRequest.request_id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>

                {/* Interested Lenders */}
                <div className="p-4">
                  <h4 className="text-sm font-bold text-white/70 uppercase mb-3">
                    Interested Lenders ({selectedRequest.interested_lenders_details?.length || 0})
                  </h4>
                  
                  {selectedRequest.interested_lenders_details?.length === 0 && (
                    <div className="text-center py-6 text-white/70">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Waiting for lender interest...</p>
                      <p className="text-xs mt-1">Lenders matching your criteria will be notified</p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {selectedRequest.interested_lenders_details?.map((lender) => (
                      <div
                        key={lender.lender_id}
                        className={`p-4 rounded-lg border ${
                          selectedLender?.lender_id === lender.lender_id
                            ? "bg-[#0A0A0A]/80 backdrop-blur-sm border-emerald-500"
                            : "bg-[#0A0A0A]/80 backdrop-blur-sm/50 border-white/[0.08]"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                            </div>
                            <div>
                              <h4 className="font-bold text-white">{lender.company_name}</h4>
                              <div className="flex items-center gap-2 text-sm text-white/70">
                                {lender.rating && (
                                  <span className="flex items-center gap-1">
                                    <Star className="w-3 h-3 text-gold" />
                                    {lender.rating.toFixed(1)}
                                  </span>
                                )}
                                <span>{lender.deals_funded || 0} deals funded</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedLender(lender);
                                fetchMessages(selectedRequest.request_id, lender.lender_id);
                              }}
                              className="border-white/[0.08] text-white/70"
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Message
                            </Button>
                            
                            {selectedRequest.status === "open" && (
                              <Button
                                size="sm"
                                onClick={() => handleSelectLender(selectedRequest.request_id, lender.lender_id)}
                                className="bg-emerald-600 hover:bg-emerald-500"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Select
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Interest Details */}
                        {lender.interest_details && (
                          <div className="mt-3 pt-3 border-t border-white/[0.08] grid grid-cols-3 gap-4 text-sm">
                            {lender.interest_details.estimated_rate && (
                              <div>
                                <span className="text-white/70">Est. Rate:</span>{" "}
                                <span className="text-white">{lender.interest_details.estimated_rate}</span>
                              </div>
                            )}
                            {lender.interest_details.estimated_points && (
                              <div>
                                <span className="text-white/70">Points:</span>{" "}
                                <span className="text-white">{lender.interest_details.estimated_points}</span>
                              </div>
                            )}
                            {lender.interest_details.proposed_terms && (
                              <div className="col-span-3">
                                <span className="text-white/70">Terms:</span>{" "}
                                <span className="text-white">{lender.interest_details.proposed_terms}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Messaging Section */}
                {selectedLender && (
                  <div className="p-4 border-t border-white/[0.08]">
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Chat with {selectedLender.company_name}
                    </h4>
                    
                    <div className="h-48 overflow-y-auto space-y-2 mb-3 bg-[#0A0A0A]/80 backdrop-blur-sm/30 rounded p-2">
                      {messages.length === 0 ? (
                        <p className="text-xs text-white/70 text-center py-4">
                          Start the conversation
                        </p>
                      ) : (
                        messages.map((msg) => (
                          <div
                            key={msg.message_id}
                            className={`p-2 rounded text-sm ${
                              msg.sender_type === "investor"
                                ? "bg-gold/20 text-gold ml-4"
                                : "bg-white/[0.08] text-white/90 mr-4"
                            }`}
                          >
                            <div className="text-xs opacity-70 mb-1">
                              {msg.sender_name || msg.sender_type}
                            </div>
                            {msg.content}
                          </div>
                        ))
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white text-sm"
                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      />
                      <Button
                        size="icon"
                        onClick={sendMessage}
                        className="gold-gradient-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-black border border-white/[0.08] rounded-lg p-8 text-center">
                <AlertCircle className="w-12 h-12 text-white/70 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">Select a Request</h3>
                <p className="text-sm text-white/70">
                  Click on a funding request to view details and manage lender communications.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-black border border-white/[0.08] rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-white mb-4">Request Funding</h3>
            
            <form onSubmit={handleCreateRequest} className="space-y-4">
              <div>
                <Label className="text-white/70">Loan Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                  <Input
                    type="number"
                    value={createForm.loan_amount_requested}
                    onChange={(e) => setCreateForm({ ...createForm, loan_amount_requested: e.target.value })}
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                    placeholder="200000"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/70">Loan Type</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {LOAN_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setCreateForm({ ...createForm, loan_type: type.id })}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-all duration-300 ${
                        createForm.loan_type === type.id
                          ? "bg-gold text-black border-gold"
                          : "border-white/[0.08] text-white/70 hover:border-white/[0.1]"
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white/70">Timeline (days)</Label>
                <Input
                  type="number"
                  value={createForm.timeline_days}
                  onChange={(e) => setCreateForm({ ...createForm, timeline_days: e.target.value })}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  placeholder="30"
                />
              </div>
              
              <div>
                <Label className="text-white/70">Notes (Optional)</Label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="w-full bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] text-white rounded-md p-2 h-20 resize-none"
                  placeholder="Additional information for lenders..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateModal(false)}
                  className="text-white/70"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="gold-gradient-btn"
                >
                  Create Request
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
